import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { hrs } from "@/lib/clipboard";
import { HAZARD_BASE, HAZARD_LABEL } from "@/lib/hazard";
import { PageShell, EmptyBox } from "@/components/PageShell";
import { EmbedPanel } from "@/components/EmbedPanel";

const TH = "text-left text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85 font-normal py-2 px-3";
const TD = "py-3 px-3 text-[12px] font-mono";

const Board = ({ title, rows, kind }) => {
  const max = Math.max(...rows.map((r) => r.median_lead_h), 1);
  return (
    <section className="border border-white/10 bg-atlas-surface/70 atlas-rise" data-testid={`leaderboard-${kind}`}>
      <h2 className="text-base md:text-lg font-display text-white px-4 pt-4 pb-2">{title}</h2>
      <table className="w-full">
        <thead><tr className="border-b border-white/10">
          <th className={TH}>#</th><th className={TH}>{kind === "hazard" ? "Hazard" : "Country"}</th>
          <th className={TH}>Median lead</th><th className={`${TH} text-right`}>Hits</th>
          <th className={`${TH} text-right hidden sm:table-cell`}>Best</th><th className={`${TH} text-right hidden sm:table-cell`}>vs news</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`lb-${kind}-row-${r.rank}`}>
              <td className={`${TD} ${r.rank === 1 ? "text-amber-300" : "text-atlas-secondary"}`}>{r.rank}</td>
              <td className={`${TD} text-white`}>
                <span className="inline-flex items-center gap-2">
                  {kind === "hazard" && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: HAZARD_BASE[r.key] || "#00F0FF" }} />}
                  {kind === "hazard" ? HAZARD_LABEL[r.key] || r.key : r.key}
                </span>
              </td>
              <td className={`${TD} w-1/3`}>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 bg-atlas-cyan/80" style={{ width: `${(r.median_lead_h / max) * 100}%`, minWidth: 4 }} />
                  <span className="text-atlas-cyan" data-testid={`lb-${kind}-median-${r.rank}`}>{hrs(r.median_lead_h)}</span>
                </div>
              </td>
              <td className={`${TD} text-right text-white`} data-testid={`lb-${kind}-hits-${r.rank}`}>{r.hits}</td>
              <td className={`${TD} text-right text-atlas-secondary hidden sm:table-cell`}>{hrs(r.best_lead_h)}</td>
              <td className={`${TD} text-right text-emerald-400 hidden sm:table-cell`}>{hrs(r.median_news_lead_h)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default function Leaderboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.leaderboard().then(setD).catch(() => setD({ by_hazard: [], by_region: [], summary: {} })); }, []);
  return (
    <PageShell testid="leaderboard-page" icon={Trophy} iconClass="text-amber-300" title="Lead-Time Leaderboard"
      intro="Where Vigilo Earth sees trouble coming earliest. Hazards and countries ranked by median hours between Vigilo Earth's flag and the first agency report, counting only confirmed first-spotted calls.">
      {d === null ? (
        <div className="text-[11px] font-mono text-atlas-secondary">Loading…</div>
      ) : d.summary.confirmed ? (
        <div className="flex flex-col gap-8">
          <div className="flex gap-10 font-mono" data-testid="leaderboard-summary">
            <div><div className="text-[9px] uppercase tracking-widest text-atlas-secondary/85">Confirmed calls</div><div className="text-2xl text-white" data-testid="lb-total">{d.summary.confirmed}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-atlas-secondary/85">Median lead</div><div className="text-2xl text-atlas-cyan">{hrs(d.summary.median_lead_h)}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-atlas-secondary/85">Best lead</div><div className="text-2xl text-amber-300">{hrs(d.summary.best_lead_h)}</div></div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Board title="By hazard" rows={d.by_hazard} kind="hazard" />
            <Board title="By country" rows={d.by_region} kind="region" />
          </div>
        </div>
      ) : (
        <EmptyBox testid="leaderboard-empty" title="No confirmed calls yet."
          text="The leaderboard fills in as soon as Vigilo Earth's first flag is verified by an agency." />
      )}
      <EmbedPanel />
    </PageShell>
  );
}
